import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import EventBusyIcon from "@mui/icons-material/EventBusy";

// Shown in place of the generic empty state when a service is blocked or only
// partially open for the selected day. `availabilityInfo` entries carry a
// `summary` string the API formats as "DAYS: status" pairs joined by ";"
// (e.g. "MON-WED: Not available; THU-FRI: 8:00 AM - 11:00 AM"), which we split
// back into a per-day schedule so the client knows when they can actually book.
function ServiceAvailabilityNotice({ availabilityInfo = [], title = "Not available on this day", actionLabel, onAction }) {
  const entries = (availabilityInfo || []).filter((i) => i?.summary);
  if (entries.length === 0) return null;

  const showLocation = entries.length > 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, px: 3, textAlign: "center" }}>
      <Box
        sx={{
          width: "5rem",
          height: "5rem",
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2
        }}
      >
        <EventBusyIcon sx={{ fontSize: "2.5rem", color: "text.secondary" }} />
      </Box>

      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ maxWidth: "22.5rem", mb: 2 }}>
        This service isn&apos;t available at the selected time. Here&apos;s when you can book it:
      </Typography>

      <Box sx={{ width: "100%", maxWidth: "22.5rem", mb: actionLabel && onAction ? 3 : 0 }}>
        {entries.map((entry) => (
          <Box
            key={`${entry.serviceId}-${entry.locationId}`}
            sx={{ mb: 1.5, p: 1.5, textAlign: "left", border: "1px solid", borderColor: "divider", borderRadius: 2 }}
          >
            {showLocation && entry.locationName && (
              <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 0.75 }}>{entry.locationName}</Typography>
            )}
            {entry.summary.split(";").map((line, idx) => {
              const [days, ...rest] = line.split(":");
              const status = rest.join(":").trim();
              return (
                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.4 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {days.trim()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {status}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export default ServiceAvailabilityNotice;
